import { useCallback, useMemo } from 'react';
import { useSession } from '@zagru/platform';

/*
 * Per-user browser storage.
 *
 * Everything this console remembers locally — which reading of a collection you prefer, your column widths
 * and filters, your starred reports, which help notes you have dismissed — is *yours*. Most of it was
 * stored under a key with no user in it, so on a shared browser the next person to sign in inherited it:
 * someone else's saved filter on the orders grid, someone else's starred reports. A remembered filter can
 * name a customer, so this is not only untidy, it is a small leak across a user boundary.
 *
 * The operating context solved this already — `contextStorage` keys the remembered warehouse by user id —
 * and this is the same rule for everything else. Keys from before the change are simply not read: a
 * preference is not worth migrating across a boundary that should have been there.
 *
 * Signed out, the key falls back to `anon`. A signed-out screen remembers nothing that matters, and the
 * alternative — writing to a shared key until the session resolves — is the bug this exists to prevent.
 *
 * **What belongs here and what does not.** Anything that holds or reveals operational data is the
 * viewer's: a grid's filters and columns, a starred report, which reading of a collection you chose,
 * which tab you left open. Pure chrome — the width of a split pane, whether a help note has been
 * dismissed — may stay on a shared key: it carries nothing about the business and nothing about the
 * person. `userStorage.test.ts` holds that line, with the chrome cases named.
 */
const PREFIX = 'zagru.u';

export function userScopedKey(userId: string | null | undefined, name: string): string {
  return `${PREFIX}.${userId ?? 'anon'}.${name}`;
}

function read(key: string): string | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* storage unavailable (private window, blocked site data): the choice lasts for this tab only */
  }
}

export interface UserStorage {
  /** The full key for `name`, for a caller that needs to pass one on (a design-system `storageKey`). */
  key: (name: string) => string;
  get: (name: string) => string | null;
  set: (name: string, value: string | null) => void;
}

/** Reads and writes this user's own browser storage. Stable across renders for the same user. */
export function useUserStorage(): UserStorage {
  const { user } = useSession();
  const userId = user?.id ?? null;
  const key = useCallback((name: string) => userScopedKey(userId, name), [userId]);
  return useMemo(() => ({ key, get: (name) => read(key(name)), set: (name, value) => write(key(name), value) }), [key]);
}
